import { Routes } from '@angular/router';
import { MsalGuard } from "@azure/msal-angular";

import { APP_401, APP_403, APP_DOCUMENTS, APP_ENTITIES, APP_ENTITY, APP_ENTITY_ASSETS, APP_ENTITY_BANKING, APP_ENTITY_DOCUMENTS, APP_ENTITY_HOLDINGS, APP_ENTITY_INFORMATION, APP_ENTITY_INVESTMENTS, APP_ENTITY_INVOICES, APP_ENTITY_LOANS, APP_ENTITY_RELATED_PARTIES, APP_ENTITY_SHARE_REGISTER, APP_INVOICE_DETAILS, APP_INVOICES, APP_INVOICES_SUMMARY, APP_MESSAGES, APP_REQUEST, APP_REQUEST_STEP, APP_REQUEST_STEPS, APP_REQUESTS, APP_REQUESTS_COMPLETED, APP_REQUESTS_IN_PROGRESS, APP_REQUESTS_IN_REVIEW, APP_REQUESTS_NEW, APP_ROOT, APP_TASK, APP_TASK_STEP, APP_TASK_STEPS, APP_TASKS } from './config/app-urls';
import { MainLayoutComponent } from "./layout/main-layout/main-layout.component";
import { ViewClientsComponent } from './view-clients/view-clients.component';
import { ViewInvoicesComponent } from './view-invoices/view-invoices.component';
import { ViewClientComponent } from './view-client/view-client.component';
import { RelatedPartiesComponent } from './view-client/related-parties/related-parties.component';
import { ClientInformationComponent } from './view-client/client-information/client-information.component';
import { ViewInvoiceDetailsComponent } from './view-invoices/view-invoice-details/view-invoice-details.component';
import { ShareRegisterComponent } from './view-client/share-register/share-register.component';
import { ClientInvoiceComponent } from './view-client/client-invoice/client-invoice.component';
import { ViewInvoiceSummariesComponent } from './view-invoices/view-invoice-summaries/view-invoice-summaries.component';
import { MessagesComponent } from './messages/messages.component';
import { DocumentsComponent } from './documents/documents.component';
import { ClientDocumentsComponent } from './view-client/client-documents/client-documents.component';
import { TasksComponent } from './tasks/tasks.component';
import { TaskComponent } from './tasks/task.component';
import { RequestsComponent } from './requests/requests.component';
import { ForbiddenPageComponent } from '@components/views/forbidden-page/forbidden-page.component';
import { AuthorizationType } from '@shared/models/authorization.model';
import { RequestTypesComponent } from './requests/request-types/request-types.component';
import { InProgressRequestsComponent } from './requests/in-progress-requests/in-progress-requests.component';
import { InReviewRequestsComponent } from './requests/in-review-requests/in-review-requests.component';
import { CompletedRequestsComponent } from './requests/completed-requests/completed-requests.component';
import { RequestComponent } from './requests/request/request.component';
import { ClientBankingComponent } from './view-client/client-banking/client-banking.component';
import { EntityAssetsComponent } from './view-client/entity-assets/entity-assets.component';
import { EntityInvestmentsComponent } from './view-client/entity-investments/entity-investments.component';
import { EntityLoansComponent } from "./view-client/entity-loans/entity-loans.component";
import { EntityHoldingsComponent } from './view-client/entity-holdings/entity-holdings.component';
import { UnauthorisedPageComponent } from "@components/views/unauthorised-page/unauthorised-page.component";
import { authorizationGuard } from "./guards/authorization.guard";
import { AppComponent } from "./app.component";
import { AdminSettingsComponent } from './admin-settings/admin-settings.component';
import { B2CLoginComponent } from './components/b2c-login/b2c-login.component';

const EXACT_PARENT_MATCH = '';
const FULL = 'full';

export const routes: Routes = [
    // Public login page for unauthenticated users
    {
        path: 'login',
        component: B2CLoginComponent
    },

    // Admin settings route (protected)
    {
        path: 'admin-settings',
        component: AdminSettingsComponent,
        canActivate: [MsalGuard, authorizationGuard],
        data: { requiresAdmin: true }
    },

    // Main application entry point.
    // Protect with MsalGuard so unauthenticated users are handled by MSAL.
    {
        path: '',
        component: AppComponent,
        canActivate: [MsalGuard],
        children: [
            {
                path: APP_ROOT.toRoutePath(),
                component: MainLayoutComponent,
                children: [
                    {
                        path: EXACT_PARENT_MATCH,
                        pathMatch: FULL,
                        redirectTo: APP_ENTITIES.toRoutePath()
                    },
                    {
                        path: APP_ENTITIES.toRoutePath(),
                        component: ViewClientsComponent,
                        canActivate: [authorizationGuard],
                        data: { authorizationRequired: AuthorizationType.Entities }
                    },
                    {
                        path: APP_ENTITY.toRoutePath(),
                        component: ViewClientComponent,
                        canActivate: [authorizationGuard],
                        data: { authorizationRequired: AuthorizationType.Entities },
                        children: [
                            {
                                path: APP_ENTITY_INFORMATION.toRoutePathRelativeTo(APP_ENTITY),
                                component: ClientInformationComponent,
                                canActivate: [authorizationGuard],
                                data: { authorizationRequired: AuthorizationType.Information }
                            },
                            {
                                path: APP_ENTITY_RELATED_PARTIES.toRoutePathRelativeTo(APP_ENTITY),
                                component: RelatedPartiesComponent,
                                canActivate: [authorizationGuard],
                                data: { authorizationRequired: AuthorizationType.RelatedParties }
                            },
                            {
                                path: APP_ENTITY_SHARE_REGISTER.toRoutePathRelativeTo(APP_ENTITY),
                                component: ShareRegisterComponent,
                                canActivate: [authorizationGuard],
                                data: { authorizationRequired: AuthorizationType.Shares }
                            },
                            {
                                path: APP_ENTITY_INVOICES.toRoutePathRelativeTo(APP_ENTITY),
                                component: ClientInvoiceComponent,
                                canActivate: [authorizationGuard],
                                data: { authorizationRequired: AuthorizationType.Invoices }
                            },
                            {
                                path: APP_ENTITY_HOLDINGS.toRoutePathRelativeTo(APP_ENTITY),
                                component: EntityHoldingsComponent
                            },
                            {
                                path: APP_ENTITY_BANKING.toRoutePathRelativeTo(APP_ENTITY),
                                component: ClientBankingComponent,
                                canActivate: [authorizationGuard],
                                data: { authorizationRequired: AuthorizationType.Banking }
                            },
                            {
                                path: APP_ENTITY_ASSETS.toRoutePathRelativeTo(APP_ENTITY),
                                component: EntityAssetsComponent,
                                canActivate: [authorizationGuard],
                                data: { authorizationRequired: AuthorizationType.Assets }
                            },
                            {
                                path: APP_ENTITY_INVESTMENTS.toRoutePathRelativeTo(APP_ENTITY),
                                component: EntityInvestmentsComponent,
                                canActivate: [authorizationGuard],
                                data: { authorizationRequired: AuthorizationType.Investments }
                            },
                            {
                                path: APP_ENTITY_LOANS.toRoutePathRelativeTo(APP_ENTITY),
                                component: EntityLoansComponent,
                                canActivate: [authorizationGuard],
                                data: { authorizationRequired: AuthorizationType.Loans }
                            },
                            {
                                path: APP_ENTITY_DOCUMENTS.toRoutePathRelativeTo(APP_ENTITY),
                                component: ClientDocumentsComponent,
                                canActivate: [authorizationGuard],
                                data: { authorizationRequired: AuthorizationType.Documents }
                            }
                        ]
                    },
                    {
                        path: APP_INVOICES.toRoutePath(),
                        component: ViewInvoicesComponent,
                        canActivate: [authorizationGuard],
                        data: { authorizationRequired: AuthorizationType.Invoices },
                        children: [
                            {
                                path: EXACT_PARENT_MATCH,
                                pathMatch: FULL,
                                redirectTo: APP_INVOICES_SUMMARY.toRoutePathRelativeTo(APP_INVOICES)
                            },
                            {
                                path: APP_INVOICES_SUMMARY.toRoutePathRelativeTo(APP_INVOICES),
                                component: ViewInvoiceSummariesComponent
                            },
                            {
                                path: APP_INVOICE_DETAILS.toRoutePathRelativeTo(APP_INVOICES),
                                component: ViewInvoiceDetailsComponent
                            }
                        ]
                    },
                    {
                        path: APP_DOCUMENTS.toRoutePath(),
                        component: DocumentsComponent,
                        canActivate: [authorizationGuard],
                        data: { authorizationRequired: AuthorizationType.Documents }
                    },
                    {
                        path: APP_MESSAGES.toRoutePath(),
                        component: MessagesComponent,
                        canActivate: [authorizationGuard],
                        data: { authorizationRequired: AuthorizationType.Messages }
                    },
                    {
                        path: APP_TASKS.toRoutePath(),
                        component: TasksComponent,
                        canActivate: [authorizationGuard],
                        data: { authorizationRequired: AuthorizationType.Tasks },
                    },
                    {
                        path: APP_TASK.toRoutePath(),
                        canActivate: [authorizationGuard],
                        data: { authorizationRequired: AuthorizationType.Tasks },
                        children: [
                            {
                                path: EXACT_PARENT_MATCH,
                                pathMatch: FULL,
                                component: TaskComponent
                            },
                            {
                                path: APP_TASK_STEPS.toRoutePathRelativeTo(APP_TASK),
                                component: TaskComponent
                            },
                            {
                                path: APP_TASK_STEP.toRoutePathRelativeTo(APP_TASK),
                                component: TaskComponent
                            }
                        ]
                    },
                    {
                        path: APP_REQUESTS.toRoutePath(),
                        component: RequestsComponent,
                        canActivate: [authorizationGuard],
                        data: { authorizationRequired: AuthorizationType.Requests },
                        children: [
                            {
                                path: EXACT_PARENT_MATCH,
                                pathMatch: FULL,
                                redirectTo: APP_REQUESTS_NEW.toRoutePathRelativeTo(APP_REQUESTS)
                            },
                            {
                                path: APP_REQUESTS_NEW.toRoutePathRelativeTo(APP_REQUESTS),
                                component: RequestTypesComponent
                            },
                            {
                                path: APP_REQUESTS_IN_PROGRESS.toRoutePathRelativeTo(APP_REQUESTS),
                                component: InProgressRequestsComponent
                            },
                            {
                                path: APP_REQUESTS_IN_REVIEW.toRoutePathRelativeTo(APP_REQUESTS),
                                component: InReviewRequestsComponent
                            },
                            {
                                path: APP_REQUESTS_COMPLETED.toRoutePathRelativeTo(APP_REQUESTS),
                                component: CompletedRequestsComponent
                            }
                        ]
                    },
                    {
                        path: APP_REQUEST.toRoutePath(),
                        canActivate: [authorizationGuard],
                        data: { authorizationRequired: AuthorizationType.Requests },
                        children: [
                            {
                                path: EXACT_PARENT_MATCH,
                                pathMatch: FULL,
                                component: RequestComponent
                            },
                            {
                                path: APP_REQUEST_STEPS.toRoutePathRelativeTo(APP_REQUEST),
                                component: RequestComponent
                            },
                            {
                                path: APP_REQUEST_STEP.toRoutePathRelativeTo(APP_REQUEST),
                                component: RequestComponent
                            }
                        ]
                    },
                    {
                        path: APP_403.toRoutePath(),
                        component: ForbiddenPageComponent
                    }
                ]
            }
        ]
    },
    {
        path: APP_401.toRoutePath(),
        component: UnauthorisedPageComponent
    },
    {
        path: '**',
        redirectTo: ''
    }
];
